import { ProfileTab, ProfileTabBlock } from "../styles";
import {
  Root,
  GamdomBlock,
  DiscordBlock,
  KickBlock,
  BlockDescript,
  SocialButton,
  GamdomBlockInfo,
  UnlinkButton,
  ButtonDescription,
  Section,
  SteamBlock,
  SteamBlockInfo,
} from "./styles";
import Image from "next/image";
import TextInput from "../components/TextInput";
import GamdomGuideModal from "components/public/ProfileModalTabs/guides/GamdomGuideModal";
import { useState } from "react";
import { useProfileContext } from "components/public/ProfileLayout/ProfileContext";
import { useConnectionsTab } from "./useConnectionsTab";
import Loader from "components/common/Loader";
import KickGuideModal from "components/public/ProfileModalTabs/guides/KickGuideModal";
import QRСodeModal from "components/ScreenComponent/MFAModals/QRСodeModal";
import { useAppContext } from "contexts";
import MFAModal from "components/ScreenComponent/MFAModals/MFAModal";
import KickSvg from "/public/images/icons/kick.svg";
import WrewardsButton from "components/wrewards-ui-kit/Button";
import CopyButton from "components/wrewards-ui-kit/Button/CopyButton";
import { hiddenText } from "helpers/hiddenText";
import useUnlinkGamdom from "hooks/user/admin/useUnlinkGamdom";
import { useDiscordVerify } from "hooks/user/useDiscordVerify";

export type ConnectionsTabProps = {
  isCanEdit?: boolean;
};

const ConnectionsTab: React.FC<ConnectionsTabProps> = ({ isCanEdit }) => {
  const {
    showQrModal,
    setShowQrModal,
    profile: user,
    onDiscordRedirect,
    onSteamRedirect,
  } = useAppContext();
  const { profile, updateUser, isOwnProfile, fetchUser, isAdminCanEdit } = useProfileContext();
  const { mutateAsync: unlinkGamdomAsync } = useUnlinkGamdom();
  const {
    isLoading,
    unlinkPlatform,
    unlinkDiscord,
    setPlatform,
    kickUsername,
    idDiscordAPIDisabled,
    discordUsername,
    platform,
    isPlatformCanConnect,
    isDiscordLinked,
    hasVerifiedRoleOnDiscordGuild,
    isVerifyLoading,
    isSteamLinked,
    steamUsername,
    unlinkSteam,
  } = useConnectionsTab(profile!);

  const { mutateAsync: verifyDiscord } = useDiscordVerify();
  const [showGamdomGuideModal, setShowGamdomGuideModal] = useState<boolean>(false);
  const [showKickGuideModal, setShowKickGuideModal] = useState<boolean>(false);
  const [showVerifyModal, setShowVerifyModal] = useState<boolean>(false);

  const isMfaEnabled = !!user?.mfaSecret;
  const getKickPatter = (displayName: string) => {
    return `/user ${displayName}`;
  };

  return profile && !isLoading ? (
    <>
      <ProfileTab>
        <ProfileTabBlock>
          <Root>
            <GamdomBlock>
              <GamdomBlockInfo>
                <Image
                  src={"/images/icons/gamdom-with-text.png"}
                  alt="gamdom"
                  width={168}
                  height={40}
                />
                <BlockDescript>Link you casino account for the best experience</BlockDescript>
              </GamdomBlockInfo>

              <GamdomBlockInfo>
                <>
                  <TextInput
                    disabled={!isOwnProfile || !!profile.gamdomId}
                    inputProps={{
                      type: "number",
                    }}
                    text={profile.gamdomId === 0 ? "" : (profile.gamdomId?.toString() ?? "")}
                    placeholder="Example: ID202020"
                    saveNewValue={(value) =>
                      updateUser({
                        field: "gamdomId",
                        value: Number(value),
                      })
                    }
                  />

                  <SocialButton
                    onClick={() => {
                      setShowGamdomGuideModal(true);
                    }}
                  >
                    How to find my ID?
                  </SocialButton>
                  {!!profile.gamdomId && isAdminCanEdit && (
                    <UnlinkButton
                      onClick={async () => {
                        await unlinkGamdomAsync({ userId: profile.id });
                        await fetchUser();
                      }}
                    >
                      unlink
                    </UnlinkButton>
                  )}
                </>
              </GamdomBlockInfo>
            </GamdomBlock>
            <Section>
              <DiscordBlock data-disabled={idDiscordAPIDisabled}>
                <Image src={"/images/icons/discord.svg"} alt="gamdom" width={56} height={56} />
                <BlockDescript>Link you discord account for the best experience</BlockDescript>
                <WrewardsButton
                  style={{ height: 50 }}
                  variant="discord-action"
                  onClick={async () => {
                    if (!isOwnProfile || isDiscordLinked) return;
                    await onDiscordRedirect();
                  }}
                >
                  {isDiscordLinked ? (
                    <>
                      {hiddenText(discordUsername ?? "")}
                      <CopyButton size="sm" color="#abbadb" copyValue={discordUsername ?? ""} />
                    </>
                  ) : isOwnProfile ? (
                    "Add Discord"
                  ) : (
                    "Not linked"
                  )}
                </WrewardsButton>

                {isDiscordLinked && isAdminCanEdit && (
                  <UnlinkButton
                    onClick={async () => {
                      await unlinkDiscord();
                      await fetchUser();
                    }}
                  >
                    unlink
                  </UnlinkButton>
                )}
                {isDiscordLinked && (
                  <>
                    <ButtonDescription>
                      Server joined and verified
                      <Image
                        src={
                          hasVerifiedRoleOnDiscordGuild
                            ? "/images/icons/check.png"
                            : "/images/icons/x-mark.png"
                        }
                        width={12}
                        height={12}
                        alt="discord_server_status"
                      />
                    </ButtonDescription>
                    {(!hasVerifiedRoleOnDiscordGuild || isAdminCanEdit) && (
                      <UnlinkButton
                        disabled={!isOwnProfile && !isAdminCanEdit}
                        onClick={async () => {
                          await verifyDiscord(profile.id);
                          await fetchUser();
                        }}
                      >
                        {isVerifyLoading ? <Loader /> : "Recheck"}
                      </UnlinkButton>
                    )}
                  </>
                )}
              </DiscordBlock>
              <KickBlock>
                <KickSvg
                  style={{ color: "#35f224 " }}
                  // src="/images/icons/kick.png"
                  width={56}
                  height={56}
                />
                {/* <Image src={"/images/icons/kick.svg"} alt="gamdom" width={56} height={56} /> */}
                <BlockDescript>Link you kick account for the best experience</BlockDescript>
                <WrewardsButton
                  style={{ height: 50 }}
                  variant="kick-action-green"
                  onClick={async () => {
                    if (!isOwnProfile || !!kickUsername) return;
                    if (!kickUsername) {
                      const isCanConnect = await isPlatformCanConnect("kick");
                      if (isCanConnect) {
                        if (isCanEdit) {
                          setShowKickGuideModal(true);
                        }
                      }
                    }
                  }}
                >
                  {!!kickUsername ? (
                    <>
                      {hiddenText(kickUsername)}
                      <CopyButton size="sm" color="#abbadb" copyValue={kickUsername ?? ""} />
                    </>
                  ) : isOwnProfile ? (
                    "Add Kick"
                  ) : (
                    "Not linked"
                  )}
                </WrewardsButton>
                {kickUsername && isAdminCanEdit && (
                  <WrewardsButton style={{ height: 50 }} variant="kick-action-green">
                    {getKickPatter(kickUsername)}
                    <CopyButton size="sm" color="#abbadb" copyValue={getKickPatter(kickUsername)} />
                  </WrewardsButton>
                )}

                {kickUsername && isAdminCanEdit && (
                  <UnlinkButton
                    onClick={(e) => {
                      e.preventDefault();
                      setPlatform("kick");
                      if (isMfaEnabled) {
                        setShowVerifyModal(true);
                      } else {
                        setShowQrModal(true);
                      }
                    }}
                  >
                    unlink
                  </UnlinkButton>
                )}
              </KickBlock>
              {/* <LootboxBlock>
                <LootboxSvg height={56} />
                <BlockDescript>Link you lootbox account for the best experience</BlockDescript>

                <GamdomBlockInfo>
                  <>
                    <TextInput
                      disabled={!isOwnProfile || !!profile.lootboxUsername}
                      inputProps={{
                        type: "text",
                      }}
                      text={profile.lootboxUsername ?? ""}
                      placeholder="Example: Lootbox Username"
                      saveNewValue={(value) =>
                        updateUser({
                          field: "lootboxUsername",
                          value: value,
                        })
                      }
                    />

                    {!!profile.lootboxUsername && isAdminCanEdit && (
                      <UnlinkButton
                        onClick={async () => {
                          await unlinkLootboxAsync({ userId: profile.id });
                          await fetchUser();
                        }}
                      >
                        unlink
                      </UnlinkButton>
                    )}
                  </>
                </GamdomBlockInfo>
              </LootboxBlock> */}
              <div style={{ width: "calc(50% - 8px)" }}>
                <SteamBlock>
                  <SteamBlockInfo>
                    <Image src="/images/icons/steam.png" alt="steam" width={60} height={60} />
                    <BlockDescript>Link you steam account for the best experience</BlockDescript>
                  </SteamBlockInfo>
                  <SteamBlockInfo>
                    <WrewardsButton
                      style={{ height: 50 }}
                      variant="steam-action"
                      onClick={async () => {
                        if (!isOwnProfile || isSteamLinked) return;
                        await onSteamRedirect();
                      }}
                    >
                      {isSteamLinked ? (
                        <>
                          {hiddenText(steamUsername ?? "")}
                          <CopyButton size="sm" color="#abbadb" copyValue={steamUsername ?? ""} />
                        </>
                      ) : isOwnProfile ? (
                        "Add Steam"
                      ) : (
                        "Not linked"
                      )}
                    </WrewardsButton>

                    {isSteamLinked && isAdminCanEdit && (
                      <UnlinkButton
                        onClick={async () => {
                          await unlinkSteam();
                          await fetchUser();
                        }}
                      >
                        unlink
                      </UnlinkButton>
                    )}
                  </SteamBlockInfo>
                </SteamBlock>
              </div>
            </Section>
          </Root>
        </ProfileTabBlock>
      </ProfileTab>

      {showGamdomGuideModal && <GamdomGuideModal onClose={() => setShowGamdomGuideModal(false)} />}
      {showKickGuideModal && <KickGuideModal onClose={() => setShowKickGuideModal(false)} />}
      {showQrModal && <QRСodeModal onClose={() => setShowQrModal(false)} />}

      {isMfaEnabled && showVerifyModal && (
        <MFAModal
          onVerify={async (otpObject) => {
            await unlinkPlatform({ platform: platform!, otp: otpObject.otp });
            await fetchUser();
          }}
          onClose={() => {
            setShowVerifyModal(false);
            setPlatform(null);
          }}
        />
      )}
    </>
  ) : (
    <Loader />
  );
};

export default ConnectionsTab;
